/**
 * Shown to an active user who isn't yet in any family.
 *
 * This is the first thing a freshly-approved user sees after
 * sign-in — before they can use the dashboard, they need to
 * create a family (or accept an invitation, in which case
 * RequireFamily redirects them to /invite/:token via the
 * sessionStorage bridge instead).
 *
 * The form is a single text input for the family name plus a
 * coral-gradient primary CTA. On success, the user is redirected
 * to /dashboard via the invalidated auth query.
 */

import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";

import { ApiError } from "@/lib/api";
import { useAuth } from "@/features/auth/useAuth";
import { useCreateFamily } from "@/features/family/useFamily";

export function CreateFamilyPage() {
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const { family, isLoading } = useAuth();
  const createFamily = useCreateFamily();
  const navigate = useNavigate();

  // Belt-and-suspenders: if the user somehow lands here already in
  // a family (e.g., two tabs racing), bounce them back to dashboard.
  useEffect(() => {
    if (!isLoading && family !== null) {
      navigate("/dashboard", { replace: true });
    }
  }, [isLoading, family, navigate]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    const trimmed = name.trim();
    if (trimmed.length === 0) {
      setError("Family name can't be empty.");
      return;
    }
    try {
      await createFamily.mutateAsync({ name: trimmed });
      // After invalidation, useAuth will return the new family and
      // the dashboard becomes reachable. Navigate now for immediate
      // feedback (useAuth may still be refetching).
      navigate("/dashboard", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.detail);
      } else {
        setError("Something went wrong. Try again?");
      }
    }
  };

  return (
    <main className="max-w-[640px] mx-auto px-6 md:px-11 pt-24 pb-16 flex flex-col gap-8">
      <div className="flex flex-col gap-3">
        <div className="font-sans text-eyebrow uppercase text-coral-deep font-semibold">
          Your family
        </div>
        <h1 className="font-serif text-display-md text-ink">
          What's your <em className="italic font-normal text-coral-deep">family</em> called?
        </h1>
        <p className="text-[15px] text-ink-muted leading-[1.6] max-w-[560px]">
          Give your family a name — something your co-parent will recognize
          when they see the invitation. You can change it later.
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-4 max-w-[560px]"
      >
        <label htmlFor="family-name" className="flex flex-col gap-2">
          <span className="font-sans text-[12px] uppercase tracking-[0.14em] text-ink-muted font-semibold">
            Family name
          </span>
          <input
            id="family-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={createFamily.isPending}
            maxLength={80}
            autoFocus
            placeholder="The Johnsons"
            className="font-sans text-[16px] text-ink bg-surface border border-edge rounded-md px-4 py-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber disabled:opacity-60"
          />
        </label>

        {error !== null && (
          <div
            role="alert"
            className="rounded-md border border-coral-deep/30 bg-coral-soft/40 px-4 py-3 font-sans text-[13px] text-coral-deep"
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={createFamily.isPending || name.trim().length === 0}
          className="self-start inline-flex items-center px-6 py-3 rounded-md font-sans font-semibold text-white shadow-md disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-amber"
          style={{
            background: "linear-gradient(135deg, var(--coral), var(--coral-deep))",
            boxShadow: "0 4px 12px rgba(251,118,84,0.35)",
          }}
        >
          {createFamily.isPending ? "Creating…" : "Create family"}
        </button>
      </form>

      <p className="font-sans text-[12px] italic text-ink-muted max-w-[560px]">
        After you create a family, you can invite one other parent from the
        Family settings page. The invitation link bypasses the super-admin
        approval queue so your co-parent can join immediately.
      </p>
    </main>
  );
}
